































import Vue from 'vue';
import {
  mapActions,
  mapState,
} from 'vuex';
import {
  HubButton,
  HubCard,
  HubIcon,
} from '@/components/HubComponents';
import statisticsServices from '@/services/hub/statistics';
import VueApexCharts from 'vue-apexcharts';
import { ApexOptions } from 'apexcharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { jsPDF as JsPdf } from 'jspdf';

export default Vue.extend({
  name: 'SimultaneousChart',

  components: {
    HubButton,
    HubCard,
    HubIcon,
    VueApexCharts,
  },

  props: {
    finalDate: {
      type: String,
      required: true,
    },
    initialDate: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      chartData: {} as Components.SimultaneousChart.ChartData,
      filter: 'year' as Services.Statistics.SimultaneousFilter,
      lastFilter: '' as Services.Statistics.SimultaneousFilter,
      nextOrPreviousFilter: {
        day: {
          next: 'hour',
          previous: 'month',
        },
        hour: {
          previous: 'day',
        },
        month: {
          next: 'day',
          previous: 'year',
        },
        year: {
          next: 'month',
        },
      } as Components.SimultaneousChart.NextOrPreviousFilter,
      oneDayInMilliseconds: 86400000, // 60 sec * 60 min * 24h * 1000 ms
      oneHourInMilliseconds: 3600000, // 60 sec * 60 min * 1000 ms
      options: {
        chart: {
          events: {
            click: (_, __, config: Components.SimultaneousChart.ChartEvents) => {
              if (config.dataPointIndex < 0) return;

              this.$data.lastFilter = this.$data.filter;

              switch (this.$data.filter) {
                case 'day': {
                  const hoursData = this.$data.chartData.day.data as number[];

                  // The number of live on this time was equal to 0
                  if (!hoursData[config.dataPointIndex]) return;

                  (this.$data.chartData as Components.SimultaneousChart.ChartData).hour = {
                    index: config.dataPointIndex,
                  };
                  break;
                }
                case 'month': {
                  const daysData = this.$data.chartData.month.data as number[];

                  // The number of live on this day was equal to 0
                  if (!daysData[config.dataPointIndex]) return;

                  this.$data.chartData.day = {
                    index: config.dataPointIndex,
                  } as Components.SimultaneousChart.DayData;
                  break;
                }
                case 'year': {
                  const month = (
                    this.$data.chartData.months[config.dataPointIndex]
                  ) as Components.SimultaneousChart.MonthData<number>;

                  if (!month.data) return; // The number of live on this month was equal to 0

                  this.$data.chartData.month = month;
                  break;
                }
                default: break;
              }

              this.$data.filter = (
                this.$data.nextOrPreviousFilter[this.$data.filter].next
                || this.$data.filter
              );
            },
          },
          toolbar: {
            show: false,
          },
          type: 'area',
          zoom: {
            enabled: false,
          },
        },
        colors: ['#46467E'],
        dataLabels: {
          enabled: true,
        },
        markers: {
          fillOpacity: 0.7,
          hover: {
            size: 13,
          },
        },
        noData: {
          text: `${this.$t('loading')}...`,
        },
        stroke: {
          curve: 'smooth',
        },
        title: { text: `${this.$t('simultaneous')}` },
        yaxis: {
          show: false,
          max: (max) => max + 1,
        },
      } as ApexOptions,
      series: [] as ApexAxisChartSeries,
      timezone: 'America/Sao_Paulo',
    };
  },

  computed: {
    ...mapState(['events', 'user']),
    beginDate(): Date {
      return new Date(`${this.initialDate} 00:00`);
    },
    endDate(): Date {
      return new Date(
        new Date(`${this.finalDate} 00:00`).getTime() + this.oneDayInMilliseconds,
      );
    },
    eventState(): Store.Events.Event {
      return this.events.event;
    },
    userState(): Store.User.State {
      return this.user;
    },
  },

  watch: {
    async finalDate(): Promise<void> {
      if (this.filter === 'year') {
        this.chartData = {
          ...this.chartData,
          beginDate: this.initialDate,
          endDate: this.finalDate,
        };

        const monthsData = await this.getSimultaneous({
          beginDate: this.beginDate,
          customerPlansId: this.eventState.customer_plans_id,
          endDate: this.endDate,
          eventHash: this.eventState.event_hash,
          filter: this.filter,
          timezone: this.timezone,
        });

        this.setMonthsData(monthsData);
      }
    },
    async initialDate(): Promise<void> {
      if (this.filter === 'year') {
        this.chartData = {
          ...this.chartData,
          beginDate: this.initialDate,
          endDate: this.finalDate,
        };

        const monthsData = await this.getSimultaneous({
          beginDate: this.beginDate,
          customerPlansId: this.eventState.customer_plans_id,
          endDate: this.endDate,
          eventHash: this.eventState.event_hash,
          filter: this.filter,
          timezone: this.timezone,
        });

        this.setMonthsData(monthsData);
      }
    },
    async filter(): Promise<void> {
      switch (this.filter) {
        case 'day': {
          if (this.lastFilter === 'month') {
            const selectedDay = this.chartData.day;
            const selectedMonth = this.chartData.month;

            const beginDate = new Date(
              selectedMonth.year || 0,
              selectedMonth.index,
              selectedDay.index + 1,
            );

            const data = await this.getSimultaneous({
              beginDate,
              customerPlansId: this.eventState.customer_plans_id,
              endDate: new Date(beginDate.getTime() + this.oneDayInMilliseconds),
              eventHash: this.eventState.event_hash,
              filter: this.filter,
              timezone: this.timezone,
            });

            this.chartData = {
              ...this.chartData,
              day: { ...this.chartData.day, data },
            };
          }

          this.setDayData();
          break;
        }
        case 'hour': {
          const selectedDay = this.chartData.day;
          const selectedHour = this.chartData.hour.index;
          const selectedMonth = this.chartData.month;

          const beginDate = new Date(
            selectedMonth.year || 0,
            selectedMonth.index,
            selectedDay.index + 1,
            selectedHour,
          );

          const data = await this.getSimultaneous({
            beginDate,
            customerPlansId: this.eventState.customer_plans_id,
            endDate: new Date(beginDate.getTime() + this.oneHourInMilliseconds),
            eventHash: this.eventState.event_hash,
            filter: this.filter,
            timezone: this.timezone,
          });

          this.setTimeData(data);

          break;
        }
        case 'month': {
          if (this.lastFilter === 'year') {
            const selectedMonth = this.chartData.month;
            const beginDate = new Date(selectedMonth.year || 0, selectedMonth.index);

            const data = await this.getSimultaneous({
              beginDate,
              customerPlansId: this.eventState.customer_plans_id,
              endDate: new Date(beginDate.getFullYear(), beginDate.getMonth() + 1),
              eventHash: this.eventState.event_hash,
              filter: this.filter,
              timezone: this.timezone,
            });

            this.chartData = {
              ...this.chartData,
              month: { ...this.chartData.month, data },
            };
          }

          this.setMonthData();
          break;
        }
        case 'year': {
          const didDateRangeChange = (
            this.initialDate !== this.chartData.beginDate
            || this.finalDate !== this.chartData.endDate
          );
          let data: number[];

          if (didDateRangeChange) {
            this.chartData = {
              ...this.chartData,
              beginDate: this.initialDate,
              endDate: this.finalDate,
            };

            data = await this.getSimultaneous({
              beginDate: this.beginDate,
              customerPlansId: this.eventState.customer_plans_id,
              endDate: this.endDate,
              eventHash: this.eventState.event_hash,
              filter: this.filter,
              timezone: this.timezone,
            });
          } else {
            const initialMonthIndex = this.chartData.months[0].index;

            data = this.chartData.months
              .sort((a) => a.index - initialMonthIndex)
              .sort((a) => a.index - initialMonthIndex)
              .map((month) => month.data) as number[];
          }

          this.setMonthsData(data);
          break;
        }
        default: break;
      }
    },
  },

  created() {
    // Adding the listener for the "onClickOnDataDownload__simultaneous" event
    // fired by the parent (ViewStatistics.vue)
    this.$parent.$on('onClickOnDataDownload__simultaneous', this.onClickOnDataDownload);
  },

  beforeDestroy() {
    // Removing the listener for the "onClickOnDataDownload__simultaneous" event
    // fired by the parent (ViewStatistics.vue)
    this.$parent.$off('onClickOnDataDownload__simultaneous');
  },

  async mounted() {
    await this.getCustomerPlan(this.eventState.customer_plans_lvw_id)
      .then((data) => { this.timezone = data.timezone || this.timezone; });

    this.chartData = {
      ...this.chartData,
      beginDate: this.initialDate,
      endDate: this.finalDate,
    };

    const monthsData = await this.getSimultaneous({
      beginDate: this.beginDate,
      customerPlansId: this.eventState.customer_plans_id,
      endDate: this.endDate,
      eventHash: this.eventState.event_hash,
      filter: this.filter,
      timezone: this.timezone,
    });

    this.setMonthsData(monthsData);
  },

  methods: {
    ...mapActions({
      loadEvent: 'events/loadEvent',
    }),
    generateCSVFile(): Blob {
      const error = `Error generating CSV file for ${this.filter} filter`;
      const type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'
        .concat('charset=utf-8');

      switch (this.filter) {
        case 'day':
        case 'month': {
          let values: number[] | string = this.chartData[this.filter].data as number[];

          if (!values || !values.length) throw new Error(error);

          values = (this.filter === 'day')
            ? values.map((_, index) => `${index}h`).join()
            : values.map((_, index) => index + 1).join();

          return new Blob([
            `${this.$t(this.filter === 'day' ? 'hours' : 'days')},`, values, '\n',
            `${this.$t('live_users')},`, this.series.map((data) => data.data).join(),
          ], { type });
        }
        case 'hour': {
          const values = Array(60).fill(0).map((value, index) => value + index);

          return new Blob([
            `${this.$t('minutes')},`, values.join(), '\n',
            `${this.$t('live_users')},`, this.series.map((data) => data.data).join(),
          ], { type });
        }
        case 'year': {
          const months = this.chartData.months.map((month) => month.label);

          if (!months || !months.length) throw new Error(error);

          return new Blob([
            `${this.$t('months')},`, months.join(), '\n',
            `${this.$t('live_users')},`, this.series.map((data) => data.data).join(),
          ], { type });
        }
        default: throw new Error(error);
      }
    },
    getCustomerPlan(
      planId: number | string,
    ): Promise<Services.User.Response.CustomerPlan> {
      return this.$store.dispatch('user/getCustomerPlan', planId);
    },
    async getSimultaneous(payload: Services.Statistics.GetSimultaneous): Promise<number[]> {
      return statisticsServices.getSimultaneous(payload)
        .then(({ data }) => data.counts)
        .catch((error) => {
          console.error('SimultaneousChart - method: "getSimultaneous()" ', error);

          this.showNotification('error_fetching_statistical_data', 'error');

          throw new Error(error);
        });
    },
    initializeOrResetMonthsArray(): void {
      this.chartData = {
        ...this.chartData,
        months: [
          {
            index: 0,
            name: `${this.$t('jan')}`,
          },
          {
            index: 1,
            name: `${this.$t('feb')}`,
          },
          {
            index: 2,
            name: `${this.$t('mar')}`,
          },
          {
            index: 3,
            name: `${this.$t('apr')}`,
          },
          {
            index: 4,
            name: `${this.$t('may')}`,
          },
          {
            index: 5,
            name: `${this.$t('jun')}`,
          },
          {
            index: 6,
            name: `${this.$t('jul')}`,
          },
          {
            index: 7,
            name: `${this.$t('aug')}`,
          },
          {
            index: 8,
            name: `${this.$t('sep')}`,
          },
          {
            index: 9,
            name: `${this.$t('oct')}`,
          },
          {
            index: 10,
            name: `${this.$t('nov')}`,
          },
          {
            index: 11,
            name: `${this.$t('dec')}`,
          },
        ],
      };
    },
    async onClickOnDataDownload(
      exportFormat: Views.ViewStatistics.ExportFormat,
    ): Promise<void> {
      const filename = `${
        this.$t('simultaneous_statistics').toString().replace(/\s/g, '_')}-${
        this.options.subtitle?.text
          ?.replace(/\//g, '-')
          .replace(' - ', '-')
          .replace(` ${this.$t('until')} `, '--')
      }`.toLowerCase();

      let file: Blob | string | null = null;

      switch (exportFormat) {
        case 'csv':
          file = this.generateCSVFile();
          break;
        case 'png':
          await html2canvas(
            this.$el.querySelector('#simultaneous-chart__print') as HTMLElement,
            {
              scale: 2,
              ignoreElements: (node) => node.classList.value.includes('previous-level-button'),
            },
          )
            .then((canvas) => { file = canvas.toDataURL('image/png'); })
            .catch((error) => console.error('Error exporting statistics in PNG format', error));
          break;
        case 'pdf':
          await html2canvas(
            this.$el.querySelector('#simultaneous-chart__print') as HTMLElement,
            {
              scale: 2,
              ignoreElements: (node) => node.classList.value.includes('previous-level-button'),
            },
          )
            .then((canvas) => {
              const pdf = new JsPdf();
              const width = pdf.internal.pageSize.getWidth();

              pdf.addImage(canvas, 5, 50, width, 0);
              pdf.save(filename);
            })
            .catch((error) => console.error('Error exporting statistics in PDF format', error));
          break;
        default: break;
      }

      if (file) saveAs(file, `${filename}.${exportFormat}`);

      this.$emit('exported');
    },
    setDayData(): void {
      const data = this.chartData.day.data as number[];
      const { year } = this.chartData.month;
      let day: number | string = this.chartData.day.index;
      let month: number | string = this.chartData.month.index;

      day = (day + 1) < 10 ? `0${day + 1}` : day + 1;
      month = (month + 1) < 10 ? `0${month + 1}` : month + 1;

      const subtitle = this.userState.settings.lang === 'en-US'
        ? `${year}/${month}/${day}`
        : `${day}/${month}/${year}`;

      this.chartData.day = {
        ...this.chartData.day,
        label: subtitle,
      };

      this.options = {
        ...this.options,
        subtitle: { text: subtitle },
        xaxis: {
          categories: data.map((_, index) => `${index}h`),
        },
      };

      this.series = [{ data, name: `${this.$t('live')}` }];
    },
    setMonthData(): void {
      const data = this.chartData.month.data as number[];

      this.options = {
        ...this.options,
        subtitle: {
          text: `${this.chartData.month.name}/${this.chartData.month.year}`,
        },
        xaxis: {
          categories: data.map((_, index) => index + 1),
        },
      };

      this.series = [{ data, name: `${this.$t('live')}` }];
    },
    setMonthsData(data: number[]): void {
      this.initializeOrResetMonthsArray();

      const splittedBeginDate = this.chartData.beginDate.split('-');
      const splittedEndDate = this.chartData.endDate.split('-');
      const finalMonthIndex = parseInt(splittedEndDate[1], 10) - 1;
      const initialMonthIndex = parseInt(splittedBeginDate[1], 10) - 1;
      const initialYear = parseInt(splittedBeginDate[0], 10);

      const max = (initialMonthIndex <= finalMonthIndex)
        ? Math.abs(initialMonthIndex - finalMonthIndex) + 1
        : Math.abs(initialMonthIndex - finalMonthIndex - 12) + 1;
      const subtitle = this.userState.settings.lang === 'en-US'
        ? `${this.chartData.beginDate.replace(/-/g, '/')} ${
          this.$t('until')} ${
          this.chartData.endDate.replace(/-/g, '/')}`
        : `${this.chartData.beginDate.split('-').reverse().join('/')} ${
          this.$t('until')} ${
          this.chartData.endDate.split('-').reverse().join('/')}`;

      data.forEach((value, index) => {
        const year = (index < initialMonthIndex)
          ? initialYear + 1
          : initialYear;

        Vue.set(this.chartData.months, index, {
          ...this.chartData.months[index],
          data: value,
          label: `${this.chartData.months[index].name}/${year.toString().slice(2)}`,
          year,
        } as Components.SimultaneousChart.MonthData<number>);
      });

      this.chartData.months
        .sort((month) => (month.index >= initialMonthIndex ? -1 : 1))
        .sort((month) => (month.index >= initialMonthIndex ? -1 : 0));

      this.options = {
        ...this.options,
        subtitle: { text: subtitle },
        xaxis: {
          categories: this.chartData.months.map((month) => month.label),
          max,
        },
      };

      this.series = [{
        data: this.chartData.months.map((month) => month.data) as number[],
        name: `${this.$t('live')}`,
      }];
    },
    setTimeData(data: number[]): void {
      let hour: number | string = this.chartData.hour.index;

      hour = hour < 10 ? `0${hour}` : hour;

      this.options = {
        ...this.options,
        subtitle: { text: `${this.chartData.day.label} - ${hour}h` },
        xaxis: {
          categories: data.map((_, index) => index),
        },
      };

      this.series = [{ data, name: `${this.$t('live')}` }];
    },
    showNotification(text: string, type: 'error' | 'success'): void {
      this.$store.dispatch('notification/create', { text, type });
    },
  },
});
